import React from 'react';

import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { setContactMessagesDirty } from '../../contacts/actions/set-contact-messages-dirty';
import { loadEmployee } from '../../employees/actions/load-employee';
import { loadEmployeeMessages } from '../../messages/actions/load-employee-messages';
import { setEmployeeOrgMessagesDirty } from '../../messages/actions/set-employee-org-messages-dirty';
import { updateEmployeeMessage } from '../../messages/actions/update-employee-message';
import { employeeSelector } from '../../reducers/employee-selectors';

import { employeeMessagesRoute } from '../../web-routes';

import { IncomingMessageNotification } from './incoming-message-notification';

import { employeeName } from '../../employees/utils/employee-name';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  employee: employeeSelector(state, ownProps.employeeId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employeeId, message }: InputProps,
) => ({
  actions: {
    loadEmployee: () => dispatch(loadEmployee(employeeId)),
    setEmployeeMessagesDirty: (orgId: number) => dispatch(setEmployeeOrgMessagesDirty(orgId)),
    loadEmployeeMessages: (orgId: number) => dispatch(loadEmployeeMessages(orgId)),
    markEmployeeMessagesRead: (employee: Employee) =>
      dispatch(
        updateEmployeeMessage(
          employee.orgId,
          employee.contactId,
          message.messageId,
          employee.employeeId,
          { isUnread: false },
        ),
      ),
  },
});

type InputProps = {
  employeeId: number;
  message: Message;
  onClose: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps & RouteComponentProps;

export const prepareEmployeeMessageNotificationData = async (employeeId: number, store: any) => {
  await store.dispatch(loadEmployee(employeeId));
  const employee = employeeSelector(store.getState(), employeeId);

  // IF (01/27/21): If an employee sends in a quick series of texts or our user has network issues,
  // this load can fail. In that case we just bail out and don't show a notification.
  if (!employee) {
    return;
  }

  await store.dispatch(setContactMessagesDirty(employee.contactId));
  await store.dispatch(setEmployeeOrgMessagesDirty(employee.orgId));
  await store.dispatch(loadEmployeeMessages(employee.orgId));
};

class EmployeeMessageNotificationComponent extends React.PureComponent<Props> {
  componentDidMount() {
    this.props.actions.loadEmployee();
  }

  componentDidUpdate() {
    this.props.actions.loadEmployee();
  }

  handleViewClick = () => {
    const { history, onClose, employee } = this.props;
    history.push(employeeMessagesRoute({ orgId: employee.orgId, employeeId: employee.employeeId }));
    onClose();
  };

  handleMarkReadClick = (orgId: number) => async () => {
    const { actions, onClose, employee } = this.props;
    await actions.markEmployeeMessagesRead(employee);
    actions.setEmployeeMessagesDirty(orgId);
    actions.loadEmployeeMessages(orgId);
    onClose();
  };

  render() {
    const { employee, message, onClose } = this.props;
    const { pathname, search } = window.location;

    // IF (01/22/20): If the user is viewing the message thread, don't show a notification
    if (
      `${pathname}${search}` ===
      employeeMessagesRoute({ orgId: employee.orgId, employeeId: employee.employeeId })
    ) {
      return null;
    }

    return (
      <IncomingMessageNotification
        messageType={message.type}
        senderName={employeeName(employee)}
        messageBody={message.messageBody}
        subtitle={employee.jobTitle || ''}
        onClose={onClose}
        onView={this.handleViewClick}
        onMarkAsRead={this.handleMarkReadClick(employee.orgId)}
      />
    );
  }
}

export const EmployeeMessageNotification = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EmployeeMessageNotificationComponent),
);
